import { QueryResult } from '@apollo/client'
import { Location, navigate } from '@reach/router'
import { startOfDay, addDays } from 'date-fns'
import { utcToZonedTime } from 'date-fns-tz'
import * as React from 'react'
import styled from 'styled-components'
import { Link } from '../../../../common/src/components/Link'
import { BodyText, Spacer, H4 } from '../../../../common/src/components/core'
import { handleError } from '../../../../common/src/data/errors'
import {
  areOnDifferentDays,
  dateNoYearFmt,
  secsAfter,
  dateMDNumberFmt,
  dateTimeTzFmt,
  dateMDYFmt,
} from '../../../../common/src/date'
import memoize from '../../../../common/src/memoize'
import {
  FetchInboxQuery,
  FetchTasksPageQuery,
  FetchThreadQuery,
  FetchShowingsQuery,
  FetchShowingsQueryVariables,
  FetchUserContextQuery,
} from '../../../../common/src/types.gen'
import { fetchShowingsQuery } from '../../graphql/queries/queries'
import LazyComponent from '../components/LazyComponent'
import { PollingQuery } from '../components/PollingQuery'
import { getContactText } from '../components/contact'
import { getHash, readHashId } from '../route'
import EventItem from './EventItem'

interface ShowingListProps {
  task: NonNullable<FetchInboxQuery['thread']>['leasingTask'] | NonNullable<FetchTasksPageQuery['task']>
  thread?: FetchThreadQuery['thread']
  filterText: string
  user: FetchUserContextQuery['self']
}

interface ShowingListState {
  startDayOffset: number
  endDayOffset: number | null
}

type ShowingsListImplProps = ShowingListProps &
  QueryResult<FetchShowingsQuery, FetchShowingsQueryVariables> & {
    isPast7DaysShown: boolean
    isFutureDaysShown: boolean
    togglePast7Days: () => void
    toggleFutureDays: () => void
    currHash: string
  }

interface ShowingsListImplState {
  // determine if we need to expand all showings when selected showing is not visible
  wasSelectedShowingVisible: boolean
  selectedId?: number
  data?: FetchShowingsQuery
}

/**
 * Data structure derived from `showings`.
 * Convenient for rendering `showings`.
 */
interface DayEvents {
  date: Date
  showings: FetchShowingsQuery['task']['showings']
  hasUpcoming: boolean
}

class ShowingsListImpl extends React.PureComponent<ShowingsListImplProps, ShowingsListImplState> {
  private handlers = {}

  constructor(props: ShowingsListImplProps) {
    super(props)
    this.state = {
      wasSelectedShowingVisible: false,
    }
  }

  static getDerivedStateFromProps(props: ShowingsListImplProps, state: ShowingsListImplState) {
    return {
      selectedId: readHashId('showing', props.currHash),
      data: props.data || state.data,
    }
  }

  componentDidUpdate() {
    const { data } = this.state
    const { wasSelectedShowingVisible, selectedId } = this.state
    if (data == null || data.task == null || data.task.showings == null || selectedId == null) {
      return
    }
    // need to update `wasSelectedShowingVisible`?
    const isSelectedShowingVisible = data.task.showings.some(s => s.id === selectedId)
    if (wasSelectedShowingVisible !== isSelectedShowingVisible) {
      this.setState({ wasSelectedShowingVisible: isSelectedShowingVisible })
    }
    // need to update showing id anchor?
    if (selectedId && !isSelectedShowingVisible) {
      this.handleShowingAnchor()
    }
  }

  /**
   * Navigate to a specified showing if the url contains showing id anchor
   * (e.g. `/leasing/inbox/thread/33777748#showing/3615450`).
   * Clear showing anchor when specfied showing becomes invisible.
   */
  private handleShowingAnchor() {
    const { togglePast7Days, toggleFutureDays, isFutureDaysShown, isPast7DaysShown, loading, filterText } = this.props
    const { wasSelectedShowingVisible } = this.state

    const filter = filterText.trim().toLowerCase()
    const isCompletelyShown = isFutureDaysShown && isPast7DaysShown && !loading

    // Selected showing is no longer visible. User either collapsed past showings, filtered, etc. Clear selection.
    const needToClearSelection = isCompletelyShown || wasSelectedShowingVisible || filter.length > 0

    if (needToClearSelection) {
      navigate('#', { replace: true }).catch(handleError)
      return
    }

    // Expand all showings to check whether selected showing id appears
    if (!isPast7DaysShown) {
      togglePast7Days()
    }
    if (!isFutureDaysShown) {
      toggleFutureDays()
    }
  }

  render() {
    const { task, filterText, isFutureDaysShown, isPast7DaysShown, toggleFutureDays, togglePast7Days, loading } =
      this.props

    const { data } = this.state

    const filter = filterText.trim().toLowerCase()

    if (loading) {
      return <BodyText>Loading showings…</BodyText>
    }
    if (data == null || data.task == null || task.address == null) {
      return null
    }

    const { tzName } = task.address.geolocation
    const { showings } = data.task
    if (showings == null) {
      return null
    }

    const { todayEvents, past7DayEvents, futureEvents } = ShowingsListImpl.calcDayEvents(showings, tzName, filter)

    return (
      <ShowingListRoot>
        <BodyText>
          <Link color="silver" onClick={togglePast7Days}>
            {(isPast7DaysShown ? 'Hide' : 'Show') + ' past 7 days'}
          </Link>
        </BodyText>
        {isPast7DaysShown && (
          <>
            <Spacer height={16} />
            {this.renderDayEvents(past7DayEvents, loading, 'No showings for past 7 days')}
          </>
        )}
        <Spacer height={16} />
        {this.renderDayEvents(todayEvents ? [todayEvents] : [], false, 'No showings today')}
        <Spacer height={16} />
        <BodyText>
          <Link color="silver" onClick={toggleFutureDays}>
            {(isFutureDaysShown ? 'Hide' : 'Show') + ' all future showings'}
          </Link>
        </BodyText>
        {isFutureDaysShown && (
          <>
            <Spacer height={16} />
            {this.renderDayEvents(futureEvents, loading, 'No future showings')}
          </>
        )}
      </ShowingListRoot>
    )
  }

  /**
   * Filter the showings and convert them to `DayEvents` for rendering.
   */
  private static calcDayEvents(
    showings: FetchShowingsQuery['task']['showings'],
    tzName: string,
    filter: string
  ): {
    todayEvents: DayEvents | null
    past7DayEvents: DayEvents[]
    futureEvents: DayEvents[]
  } {
    const now = new Date()

    // filter showings and convert them to `DayEvents`
    const dayEventsList: DayEvents[] = []

    for (const showing of showings) {
      if (!ShowingsListImpl.matchesFilter(showing, tzName, filter)) {
        continue
      }

      const showingDate = new Date(showing.timeStart)

      let currentDayEvents = dayEventsList[dayEventsList.length - 1]
      if (
        !currentDayEvents ||
        areOnDifferentDays(utcToZonedTime(currentDayEvents.date, tzName), utcToZonedTime(showingDate, tzName))
      ) {
        currentDayEvents = { date: showingDate, showings: [], hasUpcoming: false }
        dayEventsList.push(currentDayEvents)
      }
      currentDayEvents.showings.push(showing)
      if (showing.timeStart > now.toISOString()) {
        currentDayEvents.hasUpcoming = true
      }
    }

    // split `DayEvents` into past 7 days, today, and future days
    let todayEvents: DayEvents | null = null
    const past7DayEvents: DayEvents[] = []
    const futureEvents: DayEvents[] = []

    const startOfToday = startOfDay(utcToZonedTime(now, tzName))
    const startOfTomorrow = addDays(startOfToday, 1)

    for (const dayEvents of dayEventsList) {
      const zonedDayEventDate = utcToZonedTime(dayEvents.date, tzName)
      if (zonedDayEventDate < startOfToday) {
        past7DayEvents.push(dayEvents)
      } else if (zonedDayEventDate >= startOfTomorrow) {
        futureEvents.push(dayEvents)
      } else {
        todayEvents = dayEvents
      }
    }

    return { todayEvents, past7DayEvents, futureEvents }
  }

  /**
   * Converts `DayEvents` to JSX
   */
  private renderDayEvents(dayEvents: DayEvents[], loading: boolean, emptyDayEventsMessage: string) {
    const { task, thread, filterText, user } = this.props
    const { data } = this.state
    const { selectedId } = this.state
    const filter = filterText.trim().toLowerCase()

    if (data == null || data.task == null || task.address == null) {
      return null
    }

    if (dayEvents.length === 0 && loading) {
      return <BodyText>Loading...</BodyText>
    }

    if (dayEvents.length === 0 && filter.length > 0) {
      return <BodyText>{`${emptyDayEventsMessage} match your filter`}</BodyText>
    }

    if (dayEvents.length === 0) {
      return <BodyText>{emptyDayEventsMessage}</BodyText>
    }

    const { tzName } = task.address.geolocation
    const now = new Date()
    const showCal = user.permissions.canViewCalEventUrls

    return dayEvents.map((day, i) => (
      <div key={dateNoYearFmt(day.date, tzName)}>
        {i > 0 && <Spacer height={16} />}
        <H4 color={day.hasUpcoming ? 'white' : 'silver'}>{dateNoYearFmt(day.date, tzName)}</H4>
        {day.showings.map(showing => {
          const toggleArg = { showingId: showing.id, currHash: location.hash }
          return (
            <EventItem
              key={showing.id}
              showing={showing}
              highlightContactId={thread && thread.contactThem && thread.contactThem.id}
              tzName={tzName}
              taskId={task.id}
              deemphasize={secsAfter(now, showing.timeStart) > 3600}
              filterText={filter}
              noMinimize
              select={showing.id === selectedId}
              onToggleSelect={memoize(this.handlers, toggleArg, () => () => this.toggleSelectShowing(toggleArg))}
              showCal={showCal}
            />
          )
        })}
      </div>
    ))
  }

  private toggleSelectShowing({ showingId, currHash }: { showingId: number; currHash: string }) {
    const hash = getHash('showing', showingId)
    if (currHash === hash) {
      navigate('#', { replace: true }).catch(handleError) // Deselect
    } else {
      navigate(hash, { replace: true }).catch(handleError) // Select
    }
  }

  private static matchesFilter(
    showing: FetchShowingsQuery['task']['showings'][0],
    tzName: string,
    filter: string
  ): boolean {
    const ts = showing.timeStart
    if (
      dateMDNumberFmt(ts, tzName).toLowerCase().includes(filter) ||
      dateTimeTzFmt(ts, tzName).toLowerCase().includes(filter) ||
      dateMDYFmt(ts, tzName).toLowerCase().includes(filter)
    ) {
      return true
    }
    for (const sc of showing.showingContacts) {
      if (getContactText(sc.contact).toLowerCase().includes(filter)) {
        return true
      }
    }
    return false
  }
}

export const ShowingListRoot = styled.div`
  min-height: 320px;
`

/**
 * Only show today's showings by default.
 * Show past `startDayOffset` days and next `endDayOffset` days of showings upon request.
 */
export default function ShowingsList(props: ShowingListProps) {
  return <LazyComponent componentGenerator={LazyShowingsList(props)} stayVisible />
}

function LazyShowingsList(props: ShowingListProps) {
  const p = props
  // eslint-disable-next-line react/display-name
  return () => <ShowingsListComponent {...p} />
}

class ShowingsListComponent extends React.PureComponent<ShowingListProps, ShowingListState> {
  constructor(props: ShowingListProps) {
    super(props)
    this.state = {
      startDayOffset: 0,
      endDayOffset: 0,
    }
  }

  componentDidUpdate(prevProps: ShowingListProps) {
    const { task, thread } = this.props

    const prevTask = prevProps.task
    const prevThread = prevProps.thread

    const currentThreadId = thread ? thread.id : null
    const prevThreadId = prevThread ? prevThread.id : null

    // only show today's showings when task id or thread id has changed
    if (task.id !== prevTask.id || currentThreadId !== prevThreadId) {
      this.setState({ startDayOffset: 0, endDayOffset: 0 })
    }
  }

  private togglePast7Days = () => {
    this.setState(prevState => ({ startDayOffset: prevState.startDayOffset === 0 ? 7 : 0 }))
  }

  private toggleFutureDays = () => {
    this.setState(prevState => ({ endDayOffset: prevState.endDayOffset === 0 ? null : 0 }))
  }

  render() {
    const { task, thread } = this.props
    const { startDayOffset, endDayOffset } = this.state

    const variables: FetchShowingsQueryVariables = {
      taskId: task.id,
      showingsFilterInput: {
        threadId: thread == null ? null : thread.id,
        startDayOffset,
        endDayOffset,
      },
    }

    return (
      <PollingQuery query={fetchShowingsQuery} variables={variables}>
        {(res: QueryResult<FetchShowingsQuery, FetchShowingsQueryVariables>) => (
          <Location>
            {({ location }) => (
              <ShowingsListImpl
                {...this.props}
                {...res}
                isPast7DaysShown={startDayOffset === 7}
                isFutureDaysShown={endDayOffset === null}
                togglePast7Days={this.togglePast7Days}
                toggleFutureDays={this.toggleFutureDays}
                currHash={location.hash}
              />
            )}
          </Location>
        )}
      </PollingQuery>
    )
  }
}
